import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography, LinearProgress, Paper, Fade } from '@mui/material'
import { AccessTime as AccessTimeIcon } from '@mui/icons-material'

interface FloatingTimerProps {
  timeLimit: number // 制限時間（分）
  startTime: Date
  onTimeUp: () => void
}

export const FloatingTimer: React.FC<FloatingTimerProps> = ({ timeLimit, startTime, onTimeUp }) => {
  const { t } = useTranslation('test')
  const [timeLeft, setTimeLeft] = useState(timeLimit * 60) // 秒単位
  const [isWarning, setIsWarning] = useState(false)

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date()
      const elapsed = Math.floor((now.getTime() - startTime.getTime()) / 1000)
      const remaining = Math.max(0, timeLimit * 60 - elapsed)

      setTimeLeft(remaining)

      // 残り5分で警告表示
      if (remaining <= 300 && remaining > 0) {
        setIsWarning(true)
      }

      // 時間切れ
      if (remaining <= 0) {
        clearInterval(interval)
        onTimeUp()
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [timeLimit, startTime, onTimeUp])

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    } else {
      return `${minutes}:${secs.toString().padStart(2, '0')}`
    }
  }

  const getProgressValue = () => {
    const totalSeconds = timeLimit * 60
    return ((totalSeconds - timeLeft) / totalSeconds) * 100
  }

  const getColor = () => {
    if (timeLeft <= 60) return 'error' // 残り1分
    if (timeLeft <= 300) return 'warning' // 残り5分
    return 'primary'
  }

  return (
    <Fade in={true}>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          top: { xs: 8, sm: 16 },
          left: '50%',
          transform: 'translateX(-50%)',
          p: { xs: 1, sm: 1.5 },
          zIndex: 1300, // AppBarより上
          backgroundColor: isWarning ? 'secondary.light' : 'background.paper',
          border: `2px solid ${getColor() === 'error' ? '#EF4444' : getColor() === 'warning' ? '#2C6EBE' : '#1F3A5F'}`,
          borderRadius: 2,
          minWidth: { xs: '280px', sm: '320px' },
          maxWidth: { xs: '90vw', sm: '400px' },
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="center" mb={0.5}>
          <AccessTimeIcon
            sx={{
              mr: 1,
              color: getColor() + '.main',
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
            }}
          />
          <Typography
            variant="h6"
            color={getColor() + '.main'}
            fontWeight="bold"
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
              textAlign: 'center',
            }}
          >
            {t('timer.timeRemaining', { time: formatTime(timeLeft) })}
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={getProgressValue()}
          color={getColor()}
          sx={{
            height: { xs: 6, sm: 8 },
            borderRadius: 3,
            backgroundColor: 'rgba(0,0,0,0.1)',
          }}
        />

        {isWarning && timeLeft > 0 && timeLeft > 60 && (
          <Typography
            variant="caption"
            color="warning.dark"
            sx={{
              mt: 0.5,
              display: 'block',
              fontWeight: 'bold',
              textAlign: 'center',
              fontSize: { xs: '0.6875rem', sm: '0.75rem' },
            }}
          >
            {t('timer.timeApproaching')}
          </Typography>
        )}

        {timeLeft <= 60 && timeLeft > 0 && (
          <Typography
            variant="caption"
            color="error.main"
            sx={{
              mt: 0.5,
              display: 'block',
              fontWeight: 'bold',
              textAlign: 'center',
              fontSize: { xs: '0.6875rem', sm: '0.75rem' },
              animation: 'blink 1s infinite',
            }}
          >
            {t('timer.oneMinuteWarning')}
          </Typography>
        )}

        {timeLeft <= 0 && (
          <Typography
            variant="caption"
            color="error.main"
            sx={{
              mt: 0.5,
              display: 'block',
              fontWeight: 'bold',
              textAlign: 'center',
              fontSize: { xs: '0.6875rem', sm: '0.75rem' },
            }}
          >
            {t('timer.timeUpIcon')}
          </Typography>
        )}
      </Paper>
    </Fade>
  )
}
